/**
 * A tiny external store (no dependency) with a React hook. Holds the
 * app-wide state: session, runtime parameters, position, drawing mode.
 */
import { useSyncExternalStore } from 'react';
import { DEFAULT_PARAMETERS, type Me, type Parameters } from '@drawearth/shared';

export interface Position {
  lat: number;
  lng: number;
  accuracy: number;
  mock: boolean;
  ts: number;
}

export type Mode = 'browse' | 'draw' | 'playback' | 'report';

export interface AppState {
  user: Me | null;
  signedIn: boolean;
  authReady: boolean;
  params: Parameters;
  position: Position | null;
  positionError: string | null;
  colour: number;
  mode: Mode;
  playbackDate: string | null;
  excludeAgents: boolean;
  toast: string | null;
  unread: number;
}

export interface Store<T> {
  get(): T;
  set(patch: Partial<T> | ((s: T) => Partial<T>)): void;
  subscribe(cb: () => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<() => void>();
  return {
    get: () => state,
    set(patch) {
      const p = typeof patch === 'function' ? patch(state) : patch;
      state = { ...state, ...p };
      for (const s of subs) s();
    },
    subscribe(cb) {
      subs.add(cb);
      return () => subs.delete(cb);
    },
  };
}

export const appStore = createStore<AppState>({
  user: null,
  signedIn: false,
  authReady: false,
  params: { ...DEFAULT_PARAMETERS },
  position: null,
  positionError: null,
  colour: 40,
  mode: 'browse',
  playbackDate: null,
  excludeAgents: false,
  toast: null,
  unread: 0,
});

export function useApp<R>(selector: (s: AppState) => R): R {
  return useSyncExternalStore(appStore.subscribe, () => selector(appStore.get()), () => selector(appStore.get()));
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function toast(message: string, ms = 3500): void {
  appStore.set({ toast: message });
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => appStore.set({ toast: null }), ms);
}
